import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppSelector } from '../../app/hooks';
import { MagicSignRow, MagicSignStatus, AuditLogEvent, MagicSignFilters } from './types';
import { fetchMagicSignList, sendMagicSignReminder } from './MagicSignService';
import {
    MAGIC_SIGN_MAX_FILE_SIZE_MB,
    MAGIC_SIGN_ALLOWED_TYPES,
    MAGIC_SIGN_PAGE_SIZE,
    MAGIC_SIGN_PDF_ONLY_TOAST,
    buildUniqueUploadFileName,
} from './constants';
import { isReadyToGenerateLinks, deriveSigningLinks } from './utils';
import useMagicSignCommonHandlers from './useMagicSignCommonHandlers';
import useMagicSignShared from './useMagicSignShared';
import { uploadMagicSignDocument } from './uploadMagicSignDocument';
import { validOrgId } from '../MagicUpload/Utils/MagicUploadUtils';

interface UseMagicSignLogicOptions {
    /**
     * Gates the initial list + config-meta fetch.
     * Set false when the tab is not yet visible to avoid wasted calls.
     * Once the first fetch runs, results are kept in state across tab switches.
     */
    isActive?: boolean;
}

const useMagicSignLogic = ({ isActive = true }: UseMagicSignLogicOptions = {}) => {
    const [searchParams] = useSearchParams();
    const routeParams = useParams<{ id?: string }>();
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const assesseeOrgIdFromRedux = useAppSelector((state) => state.user.userData.assesseeOrgId);
    // categoryId mirrors useMnemosyneUpload: read from investor/company config staging bucket
    const investorConfig = useAppSelector((state) => state.investor.config);
    const companyConfig = useAppSelector((state) => state.company.config);
    const isCompanyContext = searchParams.get('isCompany') === 'true';
    const stagingCategoryId: string | undefined = (() => {
        const selectedConfig = isCompanyContext ? companyConfig ?? investorConfig : investorConfig;
        const id = selectedConfig?.dataConfig?.staging?.categoryId;
        return id != null ? String(id) : undefined;
    })();
    const pnoId: string = platformData?.investorOrgId ?? '';
    // 3-way fallback mirrors MagicUpload's resolveAssesseeOrgId (company-context path):
    //   1. ?orgId= query param   (set by drawer/deeplink)
    //   2. :id route param       (company dashboard route)
    //   3. redux assesseeOrgId   (borrower-side login context)
    const orgId: string = validOrgId(searchParams.get('orgId')) ?? validOrgId(routeParams.id) ?? validOrgId(assesseeOrgIdFromRedux) ?? '';

    const [rows, setRows] = useState<MagicSignRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageNo, setPageNo] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [activeRowId, setActiveRowId] = useState<string | null>(null);
    const [auditLogOpen, setAuditLogOpen] = useState(false);
    const [auditLogEvents, setAuditLogEvents] = useState<AuditLogEvent[]>([]);
    const [auditLogLoading, setAuditLogLoading] = useState(false);
    const [auditLogRowName, setAuditLogRowName] = useState('');
    const [cancelConfirmId, setCancelConfirmId] = useState<string | null>(null);
    const [docIdToView, setDocIdToView] = useState('');
    const [docNameToView, setDocNameToView] = useState('');
    const [activeFilters, setActiveFilters] = useState<MagicSignFilters>({});

    const uploadingRowsRef = useRef<Set<string>>(new Set());

    // Stale-response guard (mirrors useCommentsPagination's requestTokenRef): a slow earlier
    // fetchList call must not clobber rows with stale data if a newer call has since resolved.
    const fetchRequestTokenRef = useRef(0);

    const fetchList = useCallback(
        async (page = 0, filters?: MagicSignFilters) => {
            if (!pnoId || !orgId) return;
            const token = ++fetchRequestTokenRef.current;
            setLoading(true);
            try {
                const res = await fetchMagicSignList({
                    pnoId,
                    orgId,
                    pageNo: page,
                    pageSize: MAGIC_SIGN_PAGE_SIZE,
                    filters,
                });
                if (token !== fetchRequestTokenRef.current) return; // superseded by a newer fetchList call
                const data: MagicSignRow[] = (Array.isArray(res?.data?.data) ? res.data.data : []).map(deriveSigningLinks);
                if (page === 0) {
                    setRows(data);
                } else {
                    setRows((prev) => [...prev, ...data]);
                }
                setHasMore(data.length === MAGIC_SIGN_PAGE_SIZE);
                setPageNo(page);
            } catch (error) {
                if (token !== fetchRequestTokenRef.current) return;
                console.error('[MagicSign] Failed to load documents for signing', error);
                toast.error('Failed to load documents for signing.');
            } finally {
                if (token === fetchRequestTokenRef.current) setLoading(false);
            }
        },
        [pnoId, orgId],
    );

    const { configMeta, fetchConfigMeta, handleGenerateLinks, polusDocTypes, fetchPolusDocTypes } = useMagicSignShared<MagicSignRow>({
        pnoId,
        setRows,
        fetchList,
        activeFilters,
    });

    const hasFetchedRef = useRef(false);
    useEffect(() => {
        // Reset the fetch guard when the tab is deactivated so the next activation always
        // gets fresh data (e.g. a doc just added from the Magic Upload tab becomes visible).
        if (!isActive) {
            hasFetchedRef.current = false;
            return;
        }
        // pnoId/orgId arrive async from redux/route — don't latch the fetch guard until both are present
        if (hasFetchedRef.current || !pnoId || !orgId) return;
        hasFetchedRef.current = true;
        fetchList(0, activeFilters);
        fetchConfigMeta();
        fetchPolusDocTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive, pnoId, orgId, fetchList, fetchConfigMeta, fetchPolusDocTypes]);

    const addUploadingRow = useCallback((tempId: string, fileName: string) => {
        uploadingRowsRef.current.add(tempId);
        setRows((prev) => [{ id: tempId, fileName, status: MagicSignStatus.UPLOADING }, ...prev]);
    }, []);

    const replaceUploadingRow = useCallback((tempId: string, newRow: MagicSignRow) => {
        uploadingRowsRef.current.delete(tempId);
        setRows((prev) => prev.map((r) => (r.id === tempId ? newRow : r)));
    }, []);

    const removeUploadingRow = useCallback((tempId: string) => {
        uploadingRowsRef.current.delete(tempId);
        setRows((prev) => prev.filter((r) => r.id !== tempId));
    }, []);

    const handleFileUpload = useCallback(
        async (files: File[]) => {
            if (!files?.length || !pnoId) return;
            if (!orgId) {
                toast.error('Could not resolve company context — open Magic Sign from a company page.');
                return;
            }
            const maxBytes = MAGIC_SIGN_MAX_FILE_SIZE_MB * 1024 * 1024;
            const investorOrgId = platformData?.investorOrgId ?? '';

            for (let i = 0; i < files.length; i++) {
                const original = files[i];
                if (!MAGIC_SIGN_ALLOWED_TYPES.includes(original.type)) {
                    toast.error(MAGIC_SIGN_PDF_ONLY_TOAST);
                    continue;
                }
                if (original.size > maxBytes) {
                    toast.error(`${original.name}: File exceeds ${MAGIC_SIGN_MAX_FILE_SIZE_MB}MB limit.`);
                    continue;
                }

                // Append a timestamp suffix to the uploaded filename so a cancel + re-upload of the
                // same document is not rejected by Mnemosyne as a duplicate.
                const uniqueName = buildUniqueUploadFileName(original.name);
                const file = new File([original], uniqueName, { type: original.type });

                const tempId = `magic_sign_temp_${Date.now()}_${i}`;
                addUploadingRow(tempId, file.name);

                try {
                    const newRow = await uploadMagicSignDocument({ file, pnoId, orgId, mnemosyneOrgId: investorOrgId, stagingCategoryId });
                    replaceUploadingRow(tempId, newRow);
                } catch (error) {
                    console.error('[MagicSign] Failed to upload document', error);
                    toast.error(`Failed to upload ${file.name}`);
                    removeUploadingRow(tempId);
                }
            }
        },
        [pnoId, orgId, platformData, stagingCategoryId, addUploadingRow, replaceUploadingRow, removeUploadingRow],
    );

    // Auto-generate signing links the moment a document's three fields (Doc Type, Sign Type,
    // Signatory) are all populated while still in UPLOADED. The in-flight guard prevents a second
    // field update from firing a duplicate generate before the first completes.
    const autoGeneratingRef = useRef<Set<string>>(new Set());
    const handleMaybeAutoGenerate = useCallback(
        (row: MagicSignRow) => {
            if (!isReadyToGenerateLinks(row) || autoGeneratingRef.current.has(row.id)) return;
            autoGeneratingRef.current.add(row.id);
            handleGenerateLinks(row.id).finally(() => autoGeneratingRef.current.delete(row.id));
        },
        [handleGenerateLinks],
    );

    const handleSendReminder = useCallback(
        async (id: string) => {
            try {
                await sendMagicSignReminder({ id, logoUrl: platformData?.logoUrl });
                toast.success('Reminder sent to pending signatories.');
            } catch (error) {
                console.error('[MagicSign] Failed to send reminder', error);
                toast.error(error instanceof Error && error.message ? error.message : 'Failed to send reminder.');
            }
        },
        [platformData],
    );

    const {
        handleUpdateDocType,
        handleUpdateSignType,
        handleUpdateSignatories,
        handleCancelConfirm,
        handleViewAuditLog,
        handleDownloadDocument,
        handleCopySigningLinks,
        handleRowClick,
    } = useMagicSignCommonHandlers({
        setRows,
        setAuditLogRowName,
        setAuditLogOpen,
        setAuditLogLoading,
        setAuditLogEvents,
        setCancelConfirmId,
        setDocIdToView,
        setDocNameToView,
        setActiveRowId,
        fetchList,
        onMaybeAutoGenerate: handleMaybeAutoGenerate,
    });

    const loadMoreRows = useCallback(() => {
        if (!loading && hasMore) {
            fetchList(pageNo + 1, activeFilters);
        }
    }, [loading, hasMore, pageNo, fetchList, activeFilters]);

    const handleFiltersChange = useCallback(
        (newFilters: MagicSignFilters) => {
            setActiveFilters(newFilters);
            fetchList(0, newFilters);
        },
        [fetchList],
    );

    return {
        rows,
        loading,
        hasMore,
        configMeta,
        polusDocTypes,
        activeRowId,
        setActiveRowId,
        auditLogOpen,
        setAuditLogOpen,
        auditLogEvents,
        auditLogLoading,
        auditLogRowName,
        cancelConfirmId,
        setCancelConfirmId,
        docIdToView,
        setDocIdToView,
        docNameToView,
        activeFilters,
        handleFiltersChange,
        handleFileUpload,
        handleUpdateDocType,
        handleUpdateSignType,
        handleUpdateSignatories,
        handleGenerateLinks,
        handleSendReminder,
        handleCancelConfirm,
        handleViewAuditLog,
        handleDownloadDocument,
        handleCopySigningLinks,
        handleRowClick,
        loadMoreRows,
        refreshList: useCallback(() => fetchList(0, activeFilters), [fetchList, activeFilters]),
    };
};

export default useMagicSignLogic;
